import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CustomerDemoData as AgencyDemoData } from '../../../data';
import { useInit } from '../../Contexts/InitProvider';
import { useTemplate } from '../../features/template/TemplateProvider';
import useApi from '../../hook/useApi';

/**
 * @typedef {Object} AgencyContextType
 * @property {number} reloadKey
 * @property {(key:number)=>void} setReloadKey
 * @property {number} infoReloadKey
 * @property {(key:number)=>void} setInfoReloadKey
 * @property {Object} params
 * @property {(params:Object)=>void} setParams
 * @property {Object} agencyInfo
 * @property {(data:Object)=>void} setAgencyInfo
 * @property {()=>void} handleReload
 */

/** @type {import('react').Context<AgencyContextType|null>} */
const AgencyContext = createContext(null);

/**
 * @returns {AgencyContextType}
 */
export const useAgency = () => useContext(AgencyContext);

function AgencyProvider({ children }) {
    const { setMessage } = useTemplate();
    const [reloadKey, setReloadKey] = useState(0);
    const [infoReloadKey, setInfoReloadKey] = useState(0);
    const [params, setParams] = useState({});
    const { initInfo } = useInit();
    const { get } = useApi();

    const [agencyInfo, setAgencyInfo] = useState({
        list: AgencyDemoData,
        pagination: {}
    });

    // -- Load all agency initially
    useEffect(() => {
        setAgencyInfo({ list: AgencyDemoData, pagination: {} });
        if (Object.keys(params).length > 0) {
            get('/api/agency', {
                q: params.q,
                status: params.status,
                orderby: params['order-by'],
                sort: params.sort,
                page: params.page,
                cpp: 12
            })
                .then(({ data, error, message }) => {
                    if (error) throw new Error(message);
                    setAgencyInfo({ list: data?.agency || [], pagination: data?.pagination || {} });
                })
                .catch((err) => {
                    setAgencyInfo({ list: [], pagination: {} });
                    setMessage({ type: 'error', text: err?.message });
                });
        }
    }, [params, reloadKey]);

    const valueCtx = useMemo(() => {
        // --Handle Reload
        const handleReload = () => {
            setReloadKey((d) => d + 1);
        };

        return {
            // -- Reload Key
            reloadKey,
            setReloadKey,
            infoReloadKey,
            setInfoReloadKey,
            // -- Params
            params,
            setParams,
            // -- agency info
            agencyInfo,
            setAgencyInfo,
            // All Handler
            handleReload
        };
    }, [agencyInfo, infoReloadKey, initInfo.roles, params, reloadKey]);

    return <AgencyContext.Provider value={valueCtx}>{children}</AgencyContext.Provider>;
}

export default AgencyProvider;
